import React, { useMemo } from "react";
import * as THREE from "three";
import { GizmoHelper, useGizmoContext } from "@react-three/drei";

type PointerEv = { object: { position: THREE.Vector3 }; stopPropagation(): void };

// Body axes mapped to world space:
//   +X (Yaw)   = world [0,-1, 0]  rot [0,0,-π/2]  red
//   +Y (Roll)  = world [1, 0, 0]  rot [0,0,0]      green
//   +Z (Pitch) = world [0, 0, 1]  rot [0,-π/2,0]   blue
const GIZMO_AXES_DEF: {
    pos: [number, number, number];
    rot: [number, number, number];
    color: string;
    label: string;
    primary: boolean;
}[] = [
    { pos: [ 0,-1, 0], rot: [0, 0,-Math.PI/2], color:"#FF3333", label:"+X", primary:true  },
    { pos: [ 1, 0, 0], rot: [0, 0, 0],          color:"#CCF54E", label:"+Y", primary:true  },
    { pos: [ 0, 0, 1], rot: [0,-Math.PI/2, 0],  color:"#47B7FF", label:"+Z", primary:true  },
    { pos: [ 0, 1, 0], rot: [0, 0, Math.PI/2],  color:"#FF3333", label:"-X", primary:false },
    { pos: [-1, 0, 0], rot: [0, 0, Math.PI],    color:"#CCF54E", label:"-Y", primary:false },
    { pos: [ 0, 0,-1], rot: [0, Math.PI/2, 0],  color:"#47B7FF", label:"-Z", primary:false },
];

/** Colored circle + black label baked into a CanvasTexture. */
function makeAxisTex(label: string, bgColor: string, primary: boolean): THREE.CanvasTexture {
    const canvas = document.createElement("canvas");
    canvas.width  = 128;
    canvas.height = 128;
    const ctx = canvas.getContext("2d")!;
    ctx.beginPath();
    ctx.arc(64, 64, 52, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle   = bgColor;
    ctx.globalAlpha = primary ? 1 : 0.45;
    ctx.fill();
    if (primary) {
        ctx.globalAlpha  = 1;
        ctx.font         = "900 58px sans-serif";
        ctx.textAlign    = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle    = "#000000";
        ctx.fillText(label, 64, 68);
    }
    return new THREE.CanvasTexture(canvas);
}

const GizmoAxes: React.FC<{ scale?: number }> = ({ scale = 36 }) => {
    const { tweenCamera } = useGizmoContext();
    const textures = useMemo(
        () => GIZMO_AXES_DEF.map(({ color, label, primary }) => makeAxisTex(label, color, primary)),
        []
    );
    const shaftGeo = useMemo(() => new THREE.BoxGeometry(0.8, 0.05, 0.05), []);

    return (
        <group scale={scale}>
            {GIZMO_AXES_DEF.map(({ pos, rot, color, primary, label }, i) => (
                <group key={label}>
                    {/* shaft — box aligned along +X then rotated to body-axis world direction */}
                    <group rotation={rot}>
                        <mesh position={[0.4, 0, 0]} geometry={shaftGeo}>
                            <meshBasicMaterial color={color} transparent opacity={primary ? 1 : 0.38} toneMapped={false} />
                        </mesh>
                    </group>
                    {/* head sprite */}
                    <sprite
                        position={pos}
                        scale={primary ? 0.85 : 0.75}
                        onPointerOver={primary ? (e: PointerEv) => { e.stopPropagation(); document.body.style.cursor = "pointer"; } : undefined}
                        onPointerOut={primary ? (e: PointerEv) => { e.stopPropagation(); document.body.style.cursor = "auto"; } : undefined}
                        onPointerDown={primary ? (e: PointerEv) => { tweenCamera(e.object.position); e.stopPropagation(); } : undefined}
                    >
                        <spriteMaterial map={textures[i]} alphaTest={0.3} toneMapped={false} />
                    </sprite>
                </group>
            ))}
        </group>
    );
};

interface SatGizmoProps {
    margin?: [number, number];
    scale?: number;
}

const SatGizmo: React.FC<SatGizmoProps> = ({ margin = [90, 60], scale = 36 }) => (
    <GizmoHelper alignment="bottom-left" margin={margin}>
        <GizmoAxes scale={scale} />
    </GizmoHelper>
);

export default SatGizmo;
